<template>
  <div class="mt-5">
    <div class="mb-8">
      <div
        class="inline-flex items-center px-3 py-1 rounded-full bg-primary-light text-primary-dark text-xs font-semibold mb-4"
      >
        <span class="w-2 h-2 bg-primary-dark rounded-full mr-2 animate-pulse"></span>
        {{ t("results_badge") }}
      </div>
      <h1 class="text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900 leading-tight md:leading-10">
        {{ t("quiz_completed") }}
      </h1>
    </div>

    <transition
      enter-active-class="transition-all duration-700 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
    >
      <div v-if="animated" class="mt-8">
        <div class="rounded-2xl border border-gray-200 bg-white p-6 md:p-8 shadow-lg overflow-hidden">
          <div v-if="sellerName" class="mb-6">
            <p class="text-sm text-gray-500">{{ t("results_for") }}</p>
            <h2 class="mt-1 text-2xl font-bold text-gray-900">{{ sellerName }}</h2>
          </div>

          <transition
            enter-active-class="transition-all duration-500 ease-out"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
          >
            <div
              v-if="passed"
              class="mb-6 flex flex-col items-center justify-center p-6 rounded-xl bg-linear-to-br/srgb from-green-50 border-2 border-green-300 shadow-lg"
            >
              <h4 class="text-xl md:text-2xl font-extrabold text-green-900 mb-2">{{ t("passed_title") }}</h4>
              <p class="text-sm text-green-800 text-center">
                {{ t("passed_description", { correct: correctAnswers, total: totalQuestionsCount }) }}
              </p>
            </div>
          </transition>

          <transition
            enter-active-class="transition-all duration-500 ease-out"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
          >
            <div
              v-if="!passed"
              class="mb-6 p-5 rounded-xl bg-linear-to-r/srgb from-blue-50 border-2 border-blue-300 shadow-md"
            >
              <div class="flex items-start">
                <div>
                  <h4 class="text-lg font-bold text-blue-900 mb-2">{{ t("failed_title") }}</h4>
                  <p class="text-sm text-blue-800 mb-3">
                    {{ t("failed_description", { correct: correctAnswers, total: totalQuestionsCount }) }}
                  </p>
                  <p class="text-sm font-semibold text-blue-900">{{ t("failed_encouragement") }}</p>
                </div>
              </div>
            </div>
          </transition>

          <div class="mt-6">
            <div class="rounded-xl bg-gray-50 p-4 border border-gray-200">
              <p class="text-xs font-semibold text-gray-700 mb-3">{{ t("statistics") }}</p>
              <div class="space-y-2">
                <div class="flex justify-between items-center">
                  <span class="text-sm text-gray-600">{{ t("correct_answers") }}</span>
                  <span class="text-sm font-bold text-green-600">{{ correctAnswers }}</span>
                </div>
                <div class="flex justify-between items-center">
                  <span class="text-sm text-gray-600">{{ t("incorrect_answers") }}</span>
                  <span class="text-sm font-bold text-red-600">{{ incorrectAnswers }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <div v-if="submissionFailed" class="mt-6 rounded-xl border border-red-200 bg-red-50 p-4">
      <p class="text-sm font-semibold text-red-800">{{ t("submission_failed") }}</p>
      <GenericButton @click="submitQuizAttempt" secondary class="mt-3 w-auto px-4 py-2 text-sm">
        {{ t("submission_retry") }}
      </GenericButton>
    </div>

    <div class="mt-10 flex w-full items-center justify-between">
      <GenericButton @click="$emit('previous')" secondary class="w-auto px-6 py-2.5">
        {{ t("review_answers") }}
      </GenericButton>
      <div class="flex items-center gap-3">
        <GenericButton
          v-if="!passed"
          @click="handleRetry"
          secondary
          class="w-auto px-6 py-2.5 transition-all duration-200"
        >
          {{ t("retry") }}
        </GenericButton>
        <GenericButton @click="goToHome" class="w-auto px-6 py-2.5">
          {{ t("back_to_home") }}
        </GenericButton>
      </div>
    </div>
  </div>
</template>

<script>
import { GenericButton } from "@estaly/ui";
import { mapActions, mapState } from "vuex";
import { useI18n } from "vue-i18n";
import { QUIZ_PASSING_THRESHOLD } from "../constants";

export default {
  components: {
    GenericButton,
  },
  setup() {
    const { t } = useI18n();
    return { t };
  },
  props: {
    componentStep: {
      type: Number,
      required: true,
    },
    currentStep: {
      type: Number,
      required: true,
    },
    totalQuestions: {
      type: Number,
      required: false,
    },
    quizResults: {
      type: Object,
      default: () => ({
        correctAnswers: 0,
        totalQuestions: 0,
        sellerName: null,
      }),
    },
    selectedSeller: {
      type: Object,
      default: null,
    },
    quizId: {
      type: [Number, String],
      required: true,
    },
    answers: {
      type: Array,
      default: () => [],
    },
    attemptAlreadySubmitted: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["next", "previous", "completed", "retry", "submitted"],
  data() {
    return {
      animated: false,
      attemptSubmitted: this.attemptAlreadySubmitted,
      submissionFailed: false,
    };
  },
  computed: {
    ...mapState("store", ["selectedStoreId"]),
    correctAnswers() {
      return this.quizResults?.correctAnswers || 0;
    },
    totalQuestionsCount() {
      return this.quizResults?.totalQuestions || this.$props.totalQuestions || 0;
    },
    sellerName() {
      return this.quizResults?.sellerName;
    },
    incorrectAnswers() {
      return this.totalQuestionsCount - this.correctAnswers;
    },
    passed() {
      if (this.totalQuestionsCount === 0) return false;
      return this.correctAnswers >= Math.ceil(this.totalQuestionsCount * QUIZ_PASSING_THRESHOLD);
    },
    effectiveStoreId() {
      return this.selectedStoreId > 0 ? this.selectedStoreId : this.selectedSeller?.storeId;
    },
  },
  mounted() {
    this.animated = true;
    this.submitQuizAttempt();
  },
  methods: {
    ...mapActions("quizzes", ["submitAttempt"]),
    async submitQuizAttempt() {
      if (this.attemptSubmitted || !this.selectedSeller?.id) return;

      this.submissionFailed = false;
      try {
        const response = await this.submitAttempt({
          quizId: this.quizId,
          sellerId: this.selectedSeller.id,
          storeId: this.effectiveStoreId,
          answers: this.answers,
        });
        if (!response?.success) {
          this.submissionFailed = true;
          return;
        }
        this.attemptSubmitted = true;
        this.$emit("submitted");
      } catch {
        this.submissionFailed = true;
      }
    },
    handleRetry() {
      this.$emit("retry");
    },
    goToHome() {
      const isAdmin = this.$route.meta.layout === "admin-layout";
      this.$router.push(isAdmin ? "/admin/home" : "/pos/home");
    },
  },
};
</script>

<i18n src="./TerminationStep.i18n.json"></i18n>
